import type { Room } from "colyseus.js";
import { BoxingClient } from "./boxing";
import { ChessClient } from "./chess";
import { WordDuelClient } from "./wordduel";
import { ShredClient } from "./shred";
import { AsteroidsClient } from "./asteroids";
import { InvadersClient } from "./invaders";
import { CheckersClient } from "./checkers";
import { BubbleClient } from "./bubble";
import { TetrisClient } from "./tetris";
import { SnakeClient } from "./snake";
import { HormuzClient } from "./hormuz";
import { WormsClient } from "./worms";
import { SnlClient } from "./snl";
import { TugClient } from "./tug";
import { WordBattleClient } from "./wordbattle";

export interface ClientGameCtx {
  /** full-bleed play container (already on screen) */
  root: HTMLElement;
  room: Room;
  mySlot: number;
}

export interface ClientGame {
  mount(ctx: ClientGameCtx): void;
  unmount(): void;
  /** called after every room state change while the match view is up */
  sync?(): void;
  /** optional detail block for the results screen (final board, the word, etc.) */
  results?(ctx: ClientGameCtx): HTMLElement | null;
}

// Single source of truth for "does the web client render this game" — the
// freeze (see CLAUDE.md) means new games land in the native app only, so
// `GAMES` in shared/protocol.ts (server + both clients' registry) now
// outgrows this map. Anything not listed here has no ClientGame: it must not
// be offered as pickable in the web lobby (main.ts filters on WEB_GAME_IDS),
// and ensurePlay() falls back to a "not on web yet" panel if one is ever
// reached anyway (e.g. joining a friend's code for a native-only game).
const REGISTRY: Record<string, () => ClientGame> = {
  boxing: () => new BoxingClient(),
  chess: () => new ChessClient(),
  wordduel: () => new WordDuelClient(),
  shred: () => new ShredClient(),
  asteroids: () => new AsteroidsClient(),
  invaders: () => new InvadersClient(),
  checkers: () => new CheckersClient(),
  bubble: () => new BubbleClient(),
  tetris: () => new TetrisClient(),
  snake: () => new SnakeClient(),
  hormuz: () => new HormuzClient(),
  worms: () => new WormsClient(),
  snl: () => new SnlClient(),
  tug: () => new TugClient(),
  wordbattle: () => new WordBattleClient(),
};

/** Game ids the web client can actually render. */
export const WEB_GAME_IDS: ReadonlySet<string> = new Set(Object.keys(REGISTRY));

export function makeGameClient(gameId: string): ClientGame | null {
  return REGISTRY[gameId]?.() ?? null;
}
